/**
 * GreaseCache — Wounds/Medicine secondary heal pickup.
 *
 * Spec: `docs/specs/SPEC-wounds-and-medicine.md` §4 "Grease Caches."
 *
 * A small Victorian tin can with copper/brass styling; Basil steps on
 * it and spends the `,` (interact) key to consume a charge, which heals
 * the harness by `HARNESS.HEAL.GREASE_CACHE` integrity.
 *
 * Cadence notes from the spec:
 *   - Hollow-Knight-bench cadence: 30-second respawn after consumption
 *   - Levels 1-3 place 2-3 per level. Level 0 skips (harness never
 *     damages in the tutorial).
 *
 * Redwood opts into a floor-supported, source-authored service station.
 * Other levels retain their procedural Victorian tin until their art pass.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { ParticleSystem } from "@babylonjs/core/Particles/particleSystem";
import { PointLight } from "@babylonjs/core/Lights/pointLight";
import type { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import type { ShadowGenerator } from "@babylonjs/core/Lights/Shadows/shadowGenerator";
import type { InstantiatedModel } from "../../systems/AssetManager";
import { tagAs } from "../../levels/PlaceholderTag";
import { InteractiveObject, InteractiveConfig } from "./InteractiveObject";
import { gameEvents } from "../../core/EventBus";
import { GameEvents } from "../../config/events";
import { COLORS, HARNESS } from "../../config/GameConfig";
import type { HarnessWoundSystem } from "../../systems/HarnessWoundSystem";

/** Default respawn after consumption (seconds). */
export const GREASE_CACHE_RESPAWN_SECONDS = HARNESS.GREASE_CACHE_RESPAWN;
/** Proximity radius (world units) within which Basil's interact consumes a charge. */
export const GREASE_CACHE_INTERACT_RADIUS = HARNESS.GREASE_CACHE_INTERACT_RADIUS;
/** Amount of integrity restored per pickup. */
export const GREASE_CACHE_HEAL_AMOUNT = HARNESS.HEAL.GREASE_CACHE;

export type GreaseCacheState = "ready" | "cooldown";

export interface GreaseCacheConfig extends Omit<InteractiveConfig, "size"> {
  size?: Vector3;
  /** Override respawn seconds (tests use short cooldowns). */
  respawnSeconds?: number;
  /** Override heal amount per consumption. */
  healAmount?: number;
  /** Override interact radius. */
  interactRadius?: number;
  /** World floor for an authored station; omit to retain the legacy tin. */
  stationFloorY?: number;
  shadowGenerator?: ShadowGenerator;
}

export class GreaseCache extends InteractiveObject {
  /** Active readiness state. Exposed for tests + HUD proximity cues. */
  public cacheState: GreaseCacheState = "ready";

  /** Seconds remaining before the cache is ready again. 0 while ready. */
  private cooldownRemaining = 0;

  private readonly respawnSeconds: number;
  private readonly healAmount: number;
  private readonly interactRadius: number;

  // Procedural art
  private tinBody: Mesh | null = null;
  private tinLid: Mesh | null = null;
  private spout: Mesh | null = null;
  private dripLight: PointLight | null = null;
  private bodyMat: StandardMaterial | null = null;
  private lidMat: StandardMaterial | null = null;
  private spoutMat: StandardMaterial | null = null;
  private pulseTimer = 0;
  private wisp: ParticleSystem | null = null;
  private supportMeshes: Mesh[] = [];
  private supportMat: StandardMaterial | null = null;
  private chargeMarker: AbstractMesh | null = null;
  private shadowCasters: AbstractMesh[] = [];
  private readonly stationFloorY: number | undefined;
  private readonly shadows: ShadowGenerator | undefined;

  constructor(scene: Scene, config: GreaseCacheConfig) {
    const size = config.size ?? new Vector3(0.6, 0.55, 0.6);
    super(scene, { ...config, size });
    this.respawnSeconds = config.respawnSeconds ?? GREASE_CACHE_RESPAWN_SECONDS;
    this.healAmount = config.healAmount ?? GREASE_CACHE_HEAL_AMOUNT;
    this.interactRadius = config.interactRadius ?? GREASE_CACHE_INTERACT_RADIUS;
    this.stationFloorY = config.stationFloorY;
    this.shadows = config.shadowGenerator;

    this.buildPlaceholderGeometry();
    if (this.stationFloorY !== undefined) {
      this.mountFallback();
      void this.loadModelOverride("/assets/models/environment/grease_station.glb");
    }
  }

  // ─── Public API ──────────────────────────────────────────────────

  /** True while Basil can consume a charge. */
  get isReady(): boolean {
    return this.cacheState === "ready";
  }

  /** Seconds remaining before readiness returns. */
  get secondsUntilReady(): number {
    return this.cooldownRemaining;
  }

  /**
   * Basil attempts to consume a charge. Returns true if a charge was
   * consumed AND the harness accepted at least 1 integrity of heal (we
   * intentionally gate on a non-zero heal so spamming at full health
   * doesn't burn the cache).
   *
   * The optional `basilPosition` lets the caller enforce the interact
   * radius; callers that already pre-filtered by proximity can skip.
   */
  consume(harness: HarnessWoundSystem | null, basilPosition?: Vector3): boolean {
    if (this.cacheState !== "ready") return false;
    if (basilPosition && !this.isInInteractRange(basilPosition)) return false;
    if (!harness) return false;

    const applied = harness.heal(this.healAmount, "greaseCache");
    if (applied <= 0) return false;

    this.cacheState = "cooldown";
    this.cooldownRemaining = this.respawnSeconds;
    this.setReadyVisualState(false);

    const worldPos = this.rootMesh.getAbsolutePosition();
    gameEvents.emit(GameEvents.GREASE_CACHE_CONSUMED, {
      key: this.key,
      position: { x: worldPos.x, y: worldPos.y, z: worldPos.z },
      healAmount: applied,
    });
    return true;
  }

  /** Proximity helper for the scene's interact key router. */
  isInInteractRange(position: Vector3): boolean {
    return this.distanceTo(position) <= this.interactRadius;
  }

  /**
   * Per-frame tick driven by the scene's game loop. Handles the
   * cooldown countdown and a soft readiness pulse on the brass lid.
   */
  tick(deltaSeconds: number): void {
    if (this.cacheState === "cooldown") {
      this.cooldownRemaining = Math.max(
        0,
        this.cooldownRemaining - deltaSeconds,
      );
      if (this.cooldownRemaining <= 0) {
        this.cacheState = "ready";
        this.setReadyVisualState(true);
      }
    }
    // Breathing pulse on the brass lid while ready, flat emissive while cooled.
    this.pulseTimer += deltaSeconds;
    if (this.lidMat && this.stationFloorY === undefined && this.cacheState === "ready") {
      const pulse = 0.32 + Math.sin(this.pulseTimer * 2.2) * 0.12;
      this.lidMat.emissiveColor = Color3.FromHexString(COLORS.BRASS).scale(pulse);
    }
  }

  override onInteract(abilityId: string): boolean {
    // GreaseCaches live for Basil's interact key. We accept both
    // "interact" and "basilInteract" to stay compatible with either
    // naming convention used in level routers.
    if (abilityId === "interact" || abilityId === "basilInteract") {
      // When invoked through the generic interactive router we don't
      // have a live harness ref; defer to a nearby consume() call from
      // the level's onAbilityKey which DOES have the harness instance.
      // Return false so other interactives at the same position still
      // get a chance at the key.
      return false;
    }
    return false;
  }

  override dispose(): void {
    this.disposePlaceholderArt();
    for (const mesh of this.shadowCasters) this.shadows?.removeShadowCaster(mesh, false);
    this.shadowCasters = []; this.chargeMarker = null;
    super.dispose();
  }

  protected override onModelLoaded(instance: InstantiatedModel): void {
    this.disposePlaceholderArt();
    const offset = this.stationFloorY! - this.rootMesh.getAbsolutePosition().y;
    for (const root of instance.rootNodes) root.position.y += offset;
    this.chargeMarker = instance.meshes.find(mesh => mesh.name.endsWith("CacheCharge_marker")) ?? null;
    this.chargeMarker?.setEnabled(this.isReady);
    for (const mesh of instance.meshes) {
      mesh.isPickable = false;
      if (mesh.getTotalVertices() > 0) this.registerShadow(mesh);
    }
  }

  // ─── Private ─────────────────────────────────────────────────────

  private setReadyVisualState(ready: boolean): void {
    this.chargeMarker?.setEnabled(ready);
    const visible = ready || this.stationFloorY !== undefined;
    if (this.tinBody) this.tinBody.isVisible = visible;
    if (this.tinLid) this.tinLid.isVisible = visible;
    if (this.spout) this.spout.isVisible = visible;
    if (this.dripLight) {
      this.dripLight.intensity = ready ? 0.8 : 0;
    }
    // Flatten lid emissive while on cooldown so the "used" state reads.
    if (this.lidMat && !ready && this.stationFloorY === undefined) {
      this.lidMat.emissiveColor = Color3.FromHexString(COLORS.COPPER).scale(0.05);
    }
    // Mirror through the base-class state so highlight / affordance
    // passes treat the cooldown state as "idle".
    this.state = ready ? "active" : "idle";
    this.updateMaterialForState();
  }

  private registerShadow(mesh: AbstractMesh): void {
    mesh.receiveShadows = true;
    if (this.shadows) { this.shadows.addShadowCaster(mesh, false); this.shadowCasters.push(mesh); }
  }

  private mountFallback(): void {
    const floor = this.stationFloorY! - this.rootMesh.getAbsolutePosition().y;
    for (const mesh of [this.tinBody, this.tinLid, this.spout]) if (mesh) {
      mesh.position.y += floor + .78; mesh.position.z -= 1.03;
    }
    for (const material of [this.bodyMat, this.lidMat, this.spoutMat]) {
      if (material) material.emissiveColor = Color3.Black();
    }
    this.supportMat = new StandardMaterial(`greaseCache_${this.key}_supportMat`, this.scene);
    this.supportMat.diffuseColor = new Color3(.12, .16, .14);
    this.supportMat.specularColor = new Color3(.08, .08, .08);
    for (const [name, width, height, depth, y] of [
      ["standBase", .64, .08, .38, floor + .04],
      ["standStem", .10, .66, .10, floor + .41],
      ["standTray", .70, .08, .50, floor + .74],
    ] as const) {
      const mesh = MeshBuilder.CreateBox(`greaseCache_${this.key}_${name}`, { width, height, depth }, this.scene);
      mesh.parent = this.rootMesh; mesh.position.set(0, y, -1.03); mesh.material = this.supportMat;
      this.supportMeshes.push(mesh);
    }
    const charge = MeshBuilder.CreateBox(`greaseCache_${this.key}_fallbackCharge`,
      { width: .045, height: .16, depth: .02 }, this.scene);
    charge.parent = this.rootMesh;
    charge.position.set(0, floor + 1.02, -.77);
    charge.material = this.lidMat;
    this.chargeMarker = charge;
    this.supportMeshes.push(charge);
    for (const mesh of [this.tinBody, this.tinLid, this.spout, ...this.supportMeshes]) if (mesh) {
      mesh.isPickable = false; tagAs(mesh, "placeholder"); this.registerShadow(mesh);
    }
  }

  private disposePlaceholderArt(): void {
    this.dripLight?.dispose(); this.dripLight = null;
    this.wisp?.dispose(); this.wisp = null;
    for (const mesh of [this.tinBody, this.tinLid, this.spout, ...this.supportMeshes]) if (mesh) {
      this.shadows?.removeShadowCaster(mesh, false);
      this.shadowCasters = this.shadowCasters.filter(caster => caster !== mesh);
      mesh.dispose();
    }
    this.tinBody = this.tinLid = this.spout = null; this.supportMeshes = []; this.chargeMarker = null;
    for (const material of [this.bodyMat, this.lidMat, this.spoutMat, this.supportMat]) material?.dispose();
    this.bodyMat = this.lidMat = this.spoutMat = this.supportMat = null;
  }

  private buildPlaceholderGeometry(): void {
    const scene = this.scene;

    // Squat copper cylinder body
    this.tinBody = MeshBuilder.CreateCylinder(
      `greaseCache_${this.key}_body`,
      {
        diameterTop: 0.42,
        diameterBottom: 0.48,
        height: 0.5,
        tessellation: 24,
      },
      scene,
    );
    this.tinBody.parent = this.rootMesh;
    this.tinBody.position.y = 0.25;
    this.bodyMat = new StandardMaterial(`greaseCache_${this.key}_bodyMat`, scene);
    this.bodyMat.diffuseColor = Color3.FromHexString(COLORS.COPPER);
    this.bodyMat.specularColor = new Color3(0.35, 0.28, 0.14);
    this.bodyMat.emissiveColor = Color3.FromHexString(COLORS.COPPER).scale(0.08);
    this.tinBody.material = this.bodyMat;

    // Brass lid with a warm emissive that "breathes" while ready
    this.tinLid = MeshBuilder.CreateCylinder(
      `greaseCache_${this.key}_lid`,
      {
        diameterTop: 0.44,
        diameterBottom: 0.44,
        height: 0.07,
        tessellation: 24,
      },
      scene,
    );
    this.tinLid.parent = this.rootMesh;
    this.tinLid.position.y = 0.53;
    this.lidMat = new StandardMaterial(`greaseCache_${this.key}_lidMat`, scene);
    this.lidMat.diffuseColor = Color3.FromHexString(COLORS.BRASS);
    this.lidMat.specularColor = new Color3(0.55, 0.45, 0.15);
    this.lidMat.emissiveColor = Color3.FromHexString(COLORS.BRASS).scale(0.32);
    this.tinLid.material = this.lidMat;

    // Drip spout: thin off-center brass nub so the silhouette reads as
    // an oil can rather than a generic cylinder.
    this.spout = MeshBuilder.CreateCylinder(
      `greaseCache_${this.key}_spout`,
      {
        diameterTop: 0.04,
        diameterBottom: 0.1,
        height: 0.22,
        tessellation: 12,
      },
      scene,
    );
    this.spout.parent = this.rootMesh;
    this.spout.position = new Vector3(0.22, 0.48, 0);
    this.spout.rotation.z = -0.35;
    this.spoutMat = new StandardMaterial(`greaseCache_${this.key}_spoutMat`, scene);
    this.spoutMat.diffuseColor = Color3.FromHexString(COLORS.BRASS);
    this.spoutMat.emissiveColor = Color3.FromHexString(COLORS.BRASS).scale(0.4);
    this.spout.material = this.spoutMat;

    // Authored stations and their mounted fallback use physical materials.
    if (this.stationFloorY !== undefined) return;

    // Small warm point-light so the cache catches the eye at gameplay
    // distance even in a dim scrapyard corner.
    this.dripLight = new PointLight(
      `greaseCache_${this.key}_light`,
      Vector3.Zero(),
      scene,
    );
    this.dripLight.parent = this.rootMesh;
    this.dripLight.position = new Vector3(0.25, 0.55, 0);
    this.dripLight.diffuse = Color3.FromHexString(COLORS.BRASS);
    this.dripLight.specular = Color3.FromHexString(COLORS.COPPER);
    this.dripLight.intensity = 0.8;
    this.dripLight.range = 2.2;

    // Always-on idle particle wisp — copper-grease steam. Runs in a
    // very low-rate mode so it doesn't read as active VFX, more a
    // "this thing is warm" breadcrumb.
    try {
      const ps = new ParticleSystem(
        `greaseCache_${this.key}_wisp`,
        16,
        scene,
      );
      this.wisp = ps;
      ps.emitter = this.rootMesh;
      ps.minEmitBox = new Vector3(0.22, 0.6, -0.04);
      ps.maxEmitBox = new Vector3(0.3, 0.65, 0.04);
      ps.color1 = new Color4(0.85, 0.6, 0.25, 0.7);
      ps.color2 = new Color4(0.6, 0.4, 0.2, 0.5);
      ps.colorDead = new Color4(0.2, 0.15, 0.1, 0);
      ps.minSize = 0.05;
      ps.maxSize = 0.12;
      ps.minLifeTime = 0.8;
      ps.maxLifeTime = 1.4;
      ps.emitRate = 4;
      ps.direction1 = new Vector3(-0.1, 0.8, -0.1);
      ps.direction2 = new Vector3(0.1, 1.2, 0.1);
      ps.gravity = new Vector3(0, 0.3, 0);
      ps.blendMode = ParticleSystem.BLENDMODE_ADD;
      ps.start();
    } catch {
      // Texture-less particle init can throw on some Babylon builds; the
      // static meshes are the primary read so we swallow this failure.
    }
  }
}
